import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Box, useTheme, Fab } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { get, orderBy, take } from "lodash"
import clsx from "clsx"

import {
  NEXTOPIA_CATEGORY_TYPE,
  NEXTOPIA_POST_TYPE,
} from "extensions/one-siam/constants"

import { getIcon } from "utils/icon"

import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useFeatureDetail } from "hooks/feature/useFeatureDetail"
import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { useOccupantFloors } from "hooks/occupant/useOccupantFloors"
import { usePost } from "hooks/post/usePost"

import { FeatureDetail } from "extensions/one-siam/components/domains/features/detail/Detail"
import { FeatureSummary } from "extensions/one-siam/components/domains/features/detail/Summary"
import { SwipeableDrawer } from "components/SwipeableDrawer"
import { Head } from "components/Head"
import { FloorSelectionPanel } from "extensions/one-siam/components/domains/occupant/FloorSelectionPanel"
import { getPostDetail } from "utils/feature"
import { NextopiaSummary } from "extensions/one-siam/components/domains/features/detail/NextopiaSummary"
import { NextopiaDetail } from "extensions/one-siam/components/domains/features/detail/NextopiaDetail"
import { isValidURL } from "utils/url"
import { CorrelationSelectorSplide } from "extensions/one-siam/components/domains/features"
import { useNextopiaPlaceDetailMap } from "extensions/one-siam/hooks"

export const PlaceDetailPage = () => {
  const theme = useTheme()
  const { dataLoaded, ...data } = useFeatureDetail()

  const {
    feature,
    zone,
    logo,
    venueLogo,
    category,
    level,
    name,
    venueName,
    hours,
    id,
    websiteLink,
    gallery,
    promotions,
    privileges,
    description,
    group,
    categoryIconProp,
    correlation,
    parentCategoryRefs,
    featuredImage,
    coverImage,
  } = data

  // NOTE: This logic is specific to OneSiam for displaying Nextopia place details.
  const isNextopiaSection =
    feature?.feature_type === "section" &&
    parentCategoryRefs.includes(NEXTOPIA_CATEGORY_TYPE)

  const hasSectionFloors =
    isNextopiaSection &&
    Array.isArray(correlation?.floorOptions) &&
    correlation?.floorOptions.length > 1

  usePlaceDetailMap(feature, {
    offset: { bottom: 180 },
    enabled: !!feature && !isNextopiaSection,
  })

  useNextopiaPlaceDetailMap(feature, {
    offset: {
      bottom: 300,
    },
    zoomOffset: 0.1,
    enabled: !!feature && isNextopiaSection,
  })

  const {
    root,
    bleedWrapper,
    contentWrapper,
    backButton,
    headerWrapper,
    swipeableDrawer,
    swipeableDrawerGlobal,
    correlationSelector,
    correlationNextopiaSelector,
  } = theme.components[`AppFeatureDetail`]?.styleOverrides || {}

  const { isMultiOrdinalVenue, isMultiVenueProject, deviceType } = useVenue()
  const { goBack } = useAppUI()

  const navigate = useNavigate()

  const { floorOptions, currentFloor, currentFloorOrdinal, hasFloors } =
    useOccupantFloors(feature)

  const onNavigate = useCallback(() => {
    const locationId = feature?.properties?.feature?.id || id
    navigate(`/maps/dir/unknown/${locationId}`)
  }, [navigate, feature, id])

  const onClickMultipleFloor = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`, { replace: true })
    },
    [navigate]
  )

  const onViewAllShops = useCallback(() => {
    const parentCategories = get(
      feature,
      "properties.local_parent_categories",
      []
    )
    const venueId = get(feature, "properties.venue_id", [])
    const matchedCategory = parentCategories.find(
      (category) =>
        get(category, "properties.reference") === NEXTOPIA_CATEGORY_TYPE
    )
    const path = matchedCategory?.id
      ? `/directory?parentCategoryId=${matchedCategory.id}&subCategoryId=ALL&ordinal=ALL&venue=${venueId}`
      : null

    if (path) navigate(path, { replace: true })
  }, [feature, navigate])

  const handleHighlightClick = useCallback(
    (data) => {
      const location = data?.locationLinkInfo
      const path = location?.url
      const isExternalLink = location?.type === "custom" && isValidURL(path)
      const isNewTab = location?.is_new_tab
      const canOpenExternalLink = isExternalLink && deviceType === "mobile"

      if (!path) return

      if (canOpenExternalLink) {
        window.open(path, isNewTab ? "_blank" : "_self")
        return
      }

      navigate(path)
    },
    [deviceType, navigate]
  )

  const { t } = useTranslation()

  const { postData } = usePost(NEXTOPIA_POST_TYPE, {
    enabled: isNextopiaSection,
  })

  const sortedFloorOptions = useMemo(
    () => orderBy(correlation?.floorOptions || [], ["ordinal"], ["desc"]),
    [correlation]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <ArrowBackIcon />,
    [theme]
  )

  const transformedPosts = useMemo(
    () => take(postData?.map(getPostDetail), 5),
    [postData]
  )

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const handleCorrelationChange = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`, { replace: true })
    },
    [navigate]
  )

  const drawerBleedingHeight =
    theme.mixins[isNextopiaSection ? "nextopiaPlaceDetail" : "placeDetail"]
      ?.drawerBleeding.height

  return (
    <>
      <Head title={`${t(name)} (${t("Place Detail")})`} />
      <Box
        sx={root}
        className={clsx("AppFeatureDetail-root", parentCategoryRefs)}
      >
        <Box sx={headerWrapper} className="AppFeatureDetail-headerWrapper">
          <Fab
            variant="rounded"
            sx={backButton}
            className="AppFeatureDetail-backButton"
            onClick={handleClickBack}
          >
            {backIcon}
          </Fab>
        </Box>
        {hasFloors && (
          <Box sx={correlationSelector}>
            <FloorSelectionPanel
              floorOptions={floorOptions}
              currentFloor={currentFloor}
              currentFloorOrdinal={currentFloorOrdinal}
              onClickFloor={onClickMultipleFloor}
            />
          </Box>
        )}
        {/**
         * // TODO: Discuss whether to refactor these features into one or keep them separate (TBD).
         */}
        {hasSectionFloors && (
          <Box sx={correlationNextopiaSelector}>
            <CorrelationSelectorSplide
              className={NEXTOPIA_CATEGORY_TYPE}
              syncWithActiveSlide={false}
              selectedFeatureId={id}
              correlationOptions={sortedFloorOptions}
              onChange={handleCorrelationChange}
            />
          </Box>
        )}
        <SwipeableDrawer
          sx={swipeableDrawer}
          globalProps={{
            sx: swipeableDrawerGlobal,
          }}
          drawerBleeding={drawerBleedingHeight}
          className={clsx(
            "AppFeatureDetail-swipeableDrawer",
            parentCategoryRefs
          )}
        >
          <Box sx={bleedWrapper} className="AppFeatureDetail-bleedWrapper">
            {isNextopiaSection ? (
              <NextopiaSummary
                id={id}
                name={name}
                hours={hours}
                level={isMultiOrdinalVenue && level}
                category={category}
                venueName={venueName}
                logo={featuredImage}
                coverImage={coverImage}
                venueLogo={isMultiVenueProject && venueLogo}
                zone={zone}
                group={group}
                onNavigate={onNavigate}
                onViewAllShops={onViewAllShops}
                loading={!dataLoaded}
                categoryIconProp={categoryIconProp}
              />
            ) : (
              <FeatureSummary
                id={id}
                name={name}
                hours={hours}
                level={isMultiOrdinalVenue && level}
                category={category}
                venueName={venueName}
                logo={logo}
                venueLogo={isMultiVenueProject && venueLogo}
                zone={zone}
                group={group}
                onNavigate={onNavigate}
                loading={!dataLoaded}
                categoryIconProp={categoryIconProp}
              />
            )}
          </Box>
          <Box sx={contentWrapper}>
            {isNextopiaSection ? (
              <NextopiaDetail
                highlights={transformedPosts}
                description={description}
                onHighlightClick={handleHighlightClick}
                loading={!dataLoaded}
              />
            ) : (
              <FeatureDetail
                feature={feature}
                websiteLink={websiteLink}
                gallery={gallery}
                promotions={promotions}
                privileges={privileges}
                description={description}
                loading={!dataLoaded}
              />
            )}
          </Box>
        </SwipeableDrawer>
      </Box>
    </>
  )
}
